import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
  type RefObject,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { IconButton } from './IconButton';
import { Input } from './Input';
import { XStack } from './layout';

interface FindOverlayProps {
  isOpen: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  isSearching?: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  caseSensitive: boolean;
  onCaseSensitiveChange: (v: boolean) => void;
  currentMatch: number;
  totalMatches: number;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
}

export function FindOverlay({
  isOpen,
  inputRef: externalInputRef,
  isSearching = false,
  query,
  onQueryChange,
  caseSensitive,
  onCaseSensitiveChange,
  currentMatch,
  totalMatches,
  onNext,
  onPrevious,
  onClose,
}: FindOverlayProps) {
  const localInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? localInputRef;

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen, inputRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (isSearching) return;

      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        onPrevious();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onNext();
      }
    },
    [isSearching, onNext, onPrevious]
  );

  if (!isOpen) return null;

  const hasQuery = query.length > 0;

  return (
    <XStack
      className={cn(
        'absolute top-3 right-3 z-20 pointer-events-auto',
        'rounded-xl smoothing-default',
        'border border-border-strong',
        'bg-surface',
        'shadow-xl',
        'p-2',
        'items-center gap-1.5',
        'min-w-[320px]'
      )}
    >
      <div className="relative flex-1">
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Find"
          aria-label="Find"
          variant="onSurface"
          inputSize="sm"
          className="pr-12"
          autoFocus
        />
        {hasQuery && (totalMatches > 0 || !isSearching) && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-foreground-muted tabular-nums whitespace-nowrap pointer-events-none">
            {totalMatches > 0 ? currentMatch + 1 : 0}/{totalMatches}
          </span>
        )}
      </div>
      <IconButton
        icon={Icons.chevronUp}
        size="sm"
        variant="ghost"
        onClick={onPrevious}
        aria-label="Previous match"
        tooltip="Previous"
        tooltipOptions={{ hotkey: ['shift', 'enter'] }}
        disabled={isSearching || totalMatches === 0}
      />
      <IconButton
        icon={Icons.chevronDown}
        size="sm"
        variant="ghost"
        onClick={onNext}
        aria-label="Next match"
        tooltip="Next"
        tooltipOptions={{ hotkey: ['enter'] }}
        disabled={isSearching || totalMatches === 0}
      />
      <IconButton
        icon={Icons.caseSensitive}
        size="sm"
        variant="ghost"
        onClick={() => onCaseSensitiveChange(!caseSensitive)}
        className={cn(caseSensitive && 'bg-secondary-hover')}
        tooltip="Match case"
        aria-label="Match case"
        aria-pressed={caseSensitive}
        scale={1.15}
      />
      <IconButton
        icon={Icons.cancel}
        size="sm"
        variant="ghost"
        onClick={onClose}
        aria-label="Close find"
        tooltip="Close"
        tooltipOptions={{ hotkey: ['esc'] }}
      />
      <div role="status" aria-live="polite" className="sr-only">
        {hasQuery &&
          !isSearching &&
          (totalMatches === 0
            ? 'No matches found'
            : `Match ${currentMatch + 1} of ${totalMatches}`)}
      </div>
    </XStack>
  );
}
